import * as React from 'react';
import { FormGroup, Input, Label } from 'reactstrap';
import { DARK_PLACEHOLDER, getNewValue, ROUTE_PATH } from '../../../constants';
import { OBVirtualSensorConfig, OptionType } from '../../../types';
import { useDispatch, useSelector } from 'react-redux';
import { ONBOARDING_ACTIONS } from '../../../actions/onboarding';
import { apiRequest } from '../../../APIClient';
import {
  DEFAULT_AVAILABLE_VIRTUAL_SENSORS,
  DEFAULT_VIRTUAL_SENSOR_CONFIG,
  onboardingSelector,
} from '../../../reducers/onboarding';
import {
  featureFlagSelector,
  sessionSelector,
} from '../../../reducers/session';
import InputDropdown from '../../../widgets/InputDropdown';
import { ValueType } from 'react-select/src/types';
import WarningAlert from '../../../widgets/WarningAlert';

type Props = {
  vSensorQuota?: number;
  vSensorUsed?: number;
};

export const OnboardingVirtualSensorConfig: React.FC<Props> = ({
  vSensorQuota = 0,
  vSensorUsed = 0,
}) => {
  const config = useSelector(onboardingSelector.getVirtualSensorConfig);
  const canEdit = useSelector(sessionSelector.canEditOnboarding);
  const availableVirtualSensors = useSelector(
    onboardingSelector.getAvailableVirtualSensors,
  );
  const virtualSensorsEnabled = useSelector(
    featureFlagSelector.onboarding.virtualSensors,
  );
  const orgID = useSelector(sessionSelector.getRiskID);
  const orgIDParam = orgID ? `?id=${orgID}` : '';

  const dispatch = useDispatch();
  React.useEffect(() => {
    if (config === DEFAULT_VIRTUAL_SENSOR_CONFIG) {
      dispatch(apiRequest.getOBVirtualSensorSection());
    }
    if (availableVirtualSensors === DEFAULT_AVAILABLE_VIRTUAL_SENSORS) {
      dispatch(apiRequest.getOBAvailableVirtualSensors());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const [availableVirtualSensorsList, setAvailiableVirtualSensorsList] =
    React.useState<OptionType[]>(
      availableVirtualSensors.sensors.map((aep) => ({
        label: aep.name,
        value: aep.value,
      })),
    );

  const [hypervisors, setHypervisors] = React.useState<OptionType[]>(
    config.hypervisor?.map((h) => ({
      value: h,
      label:
        availableVirtualSensors?.sensors?.find((as) => as.value === h)?.name ??
        h,
    })) ?? [],
  );

  const [virtualizationRequirements, setVirtualizationRequirements] =
    React.useState(config.virtualizationRequirements);
  const [noVirtualSensor, setNoVirtualSensor] = React.useState(
    config.noVirtualSensor,
  );
  const [isUpdated, setIsUpdated] = React.useState(false);

  const overrideSettings = React.useCallback(
    (overrides: Partial<OBVirtualSensorConfig>) => {
      const config: OBVirtualSensorConfig = {
        hypervisor: hypervisors.map((h) => h.value),
        virtualizationRequirements,
        noVirtualSensor,
        completed: noVirtualSensor,
      };
      return {
        ...config,
        ...overrides,
      };
    },
    [hypervisors, virtualizationRequirements, noVirtualSensor],
  );

  React.useEffect(() => {
    if (
      !isUpdated &&
      config !== DEFAULT_VIRTUAL_SENSOR_CONFIG &&
      availableVirtualSensors !== DEFAULT_AVAILABLE_VIRTUAL_SENSORS
    ) {
      setAvailiableVirtualSensorsList([
        ...availableVirtualSensors.sensors.map((ep) => ({
          label: ep.name,
          value: ep.value,
        })),
      ]);
      setHypervisors(
        config.hypervisor?.map((h) => ({
          value: h,
          label:
            availableVirtualSensors?.sensors?.find((as) => as.value === h)
              ?.name ?? h,
        })) ?? [],
      );
      setVirtualizationRequirements(config.virtualizationRequirements);
      setNoVirtualSensor(config.noVirtualSensor);
      setIsUpdated(true);
    }
  }, [
    isUpdated,
    config,
    availableVirtualSensors,
    availableVirtualSensorsList,
    setNoVirtualSensor,
    setVirtualizationRequirements,
    setIsUpdated,
  ]);

  const handleSelectChange = React.useCallback(
    (value: ValueType<OptionType>) => {
      const newValue = getNewValue(value, false);
      setHypervisors([newValue] as OptionType[]);
      dispatch(
        ONBOARDING_ACTIONS.EDIT_VIRTUAL_SENSOR(
          overrideSettings({
            hypervisor: newValue ? [newValue.value] : [],
          }),
        ),
      );
      dispatch(apiRequest.saveOBVirtualSensorSection());
    },
    [dispatch, overrideSettings],
  );

  const virtualizationRequirementsCompletedChange = React.useCallback(
    (e) => {
      const newValue = e.currentTarget.checked;
      setVirtualizationRequirements(newValue);
      dispatch(
        ONBOARDING_ACTIONS.EDIT_VIRTUAL_SENSOR(
          overrideSettings({
            completed: newValue,
            virtualizationRequirements: newValue,
          }),
        ),
      );
      dispatch(apiRequest.saveOBVirtualSensorSection());
    },
    [dispatch, overrideSettings, setVirtualizationRequirements],
  );

  React.useEffect(() => {
    if (config.noVirtualSensor) {
      setHypervisors([]);
      setVirtualizationRequirements(false);
    }
    setNoVirtualSensor(config.noVirtualSensor);
  }, [
    config,
    setVirtualizationRequirements,
    setHypervisors,
    setNoVirtualSensor,
  ]);

  return (
    <FormGroup className="pl-2">
      <div className="ob-section-description">
        {virtualSensorsEnabled && (
          <p data-cy="vsensor-quota">
            {vSensorUsed} of {vSensorQuota} activated
            {vSensorQuota - vSensorUsed > 0
              ? ` (${vSensorQuota - vSensorUsed} available)`
              : ''}
          </p>
        )}
        <p>
          {virtualSensorsEnabled
            ? 'Provide and confirm configuration details for all virtual sensors. ' +
              'Virtual sensors inspect network traffic using your own hypervisor.'
            : 'Provide and confirm configuration details for all virtual log ' +
              'collectors. Virtual log collectors perform on-premise log collection.'}
        </p>
        {virtualSensorsEnabled && (
          <WarningAlert data-cy="warningAlert">
            The MDR subscription service includes a single virtual 100 series
            vSensor. vSensors are designed to support customers with virtual
            firewalls and do not replace the need for any scoped physical
            sensors.
          </WarningAlert>
        )}
      </div>
      <h5 className="pb-1">Virtual Infrastructure Requirements</h5>
      <InputDropdown
        menuPortalTarget={document.body}
        id="onHypervisor"
        data-cy="onHypervisor"
        isCreatable={false}
        showDropdownArrow={true}
        onChange={(value) => {
          handleSelectChange(value);
        }}
        value={hypervisors}
        options={availableVirtualSensorsList}
        placeholder="Choose a hypervisor"
        styles={DARK_PLACEHOLDER}
        isDisabled={!canEdit || noVirtualSensor}
        classNamePrefix="onHypervisor"
      />
      <h5 className="mt-3 pb-1">Virtual Machine Requirements</h5>
      <div style={{ display: 'grid', paddingTop: '10px' }}>
        <Label className="ob-accordion-checkbox" check>
          <Input
            type="checkbox"
            id="sensorsRequirementsMet"
            checked={virtualizationRequirements}
            onChange={virtualizationRequirementsCompletedChange}
            disabled={noVirtualSensor || !canEdit}
          />
          {virtualSensorsEnabled
            ? 'By checking this, I confirm that each virtual sensor will be able to meet the following minimum requirements:'
            : 'Each virtual log collector meets the following minimum requirements:'}
        </Label>
        {virtualSensorsEnabled ? (
          <>
            <ul style={{ fontSize: '14px' }}>
              <li>8 vCPUs</li>
              <li>16 GB of RAM</li>
              <li>40 GB of storage space</li>
            </ul>
            <div className="pt-3">
              Go to{' '}
              <a
                href={ROUTE_PATH.DOWNLOADS.UNIFIED_PORTAL + orgIDParam}
                target="_blank"
                rel="noopener noreferrer"
              >
                Downloads
              </a>{' '}
              to begin downloading and installing virtual sensors.
            </div>
          </>
        ) : (
          <>
            <ul style={{ fontSize: '14px' }}>
              <li>2-core processor</li>
              <li>4 GB of memory</li>
              <li>40 GB of available space</li>
            </ul>
            <div className="pt-3">
              Go to{' '}
              <a
                href={ROUTE_PATH.DOWNLOADS.UNIFIED_PORTAL + orgIDParam}
                target="_blank"
                rel="noopener noreferrer"
              >
                Downloads
              </a>{' '}
              to begin downloading and installing virtual log collectors.
            </div>
          </>
        )}
      </div>
    </FormGroup>
  );
};

export const VirtualSensorConfigFooter = () => {
  const dispatch = useDispatch();
  const settings = useSelector(onboardingSelector.getVirtualSensorConfig);
  const { hypervisor, virtualizationRequirements, noVirtualSensor } = settings;
  const canEdit = useSelector(sessionSelector.canEditOnboarding);
  const virtualSensorsEnabled = useSelector(
    featureFlagSelector.onboarding.virtualSensors,
  );

  const noVirtualSensorChange = React.useCallback(
    (e) => {
      if (e.currentTarget.checked) {
        dispatch(
          ONBOARDING_ACTIONS.EDIT_VIRTUAL_SENSOR({
            noVirtualSensor: e.currentTarget.checked,
            virtualizationRequirements: false,
            hypervisor: [],
            completed: true, // true because no virtual sensor is checked
          }),
        );
      } else {
        dispatch(
          ONBOARDING_ACTIONS.EDIT_VIRTUAL_SENSOR({
            hypervisor,
            virtualizationRequirements,
            noVirtualSensor: e.currentTarget.checked,
            completed: false, // false because virtual sensor is not checked
          }),
        );
      }
      dispatch(apiRequest.saveOBVirtualSensorSection());
    },
    [dispatch, virtualizationRequirements, hypervisor],
  );

  return (
    <Label className="ob-accordion-checkbox" check>
      <Input
        type="checkbox"
        id="noVirtualSensors"
        data-cy="noVirtualSensors"
        checked={noVirtualSensor}
        disabled={!canEdit}
        onChange={noVirtualSensorChange}
      />
      {virtualSensorsEnabled
        ? 'I do not need to deploy a vSensor at this time'
        : "I don't want to use virtual log collectors"}
    </Label>
  );
};

export default OnboardingVirtualSensorConfig;
